/**
 * Reactions.jsx — the reaction bar and its picker.
 *
 * The five reactions are chosen for what clinicians actually want to say about
 * a colleague's post, not borrowed from a consumer network. "Celebrate" and
 * "Funny" have no place under a post about a missed diagnosis; "Agree" and
 * "Important" carry real professional meaning and are the two things people
 * most often want to express and cannot, with a single like.
 *
 * There is deliberately no negative reaction. A downvote on a clinical post is
 * a judgement with no reasoning attached, and on a platform where people post
 * about uncertainty and error it would reliably punish the honesty the whole
 * thing depends on. Disagreement belongs in a comment, where it has to be
 * argued for and attributed.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  ThumbsUp, CheckCheck, Lightbulb, AlertCircle, HeartHandshake,
} from 'lucide-react';
import { compactNumber } from '../lib/format';

export const REACTIONS = {
  useful:     { label: 'Useful',     icon: ThumbsUp,       colour: '#2C5C91' },
  agree:      { label: 'Agree',      icon: CheckCheck,     colour: '#2F7D4F' },
  insightful: { label: 'Insightful', icon: Lightbulb,      colour: '#8A5A12' },
  important:  { label: 'Important',  icon: AlertCircle,    colour: '#96402F' },
  thanks:     { label: 'Thanks',     icon: HeartHandshake, colour: '#5A3364' },
};

export const REACTION_ORDER = ['useful', 'agree', 'insightful', 'important', 'thanks'];

/* ── Summary shown above the action bar ────────────────────────────────────── */

/**
 * The stacked icons and total, as on most professional networks.
 *
 * Renders nothing at zero rather than showing "0 reactions", which reads as
 * failure on a post that is simply new.
 */
export function ReactionSummary({ counts = {}, total = 0, onOpen }) {
  const present = REACTION_ORDER.filter((k) => (counts[k] ?? 0) > 0).slice(0, 3);
  if (!total || !present.length) return null;

  return (
    <button type="button" className="rx-summary" onClick={onOpen}>
      <span className="rx-summary__icons" aria-hidden="true">
        {present.map((k) => {
          const R = REACTIONS[k];
          const Icon = R.icon;
          return (
            <span key={k} className="rx-summary__chip" style={{ background: R.colour }}>
              <Icon size={10} strokeWidth={2.75} />
            </span>
          );
        })}
      </span>
      <span className="rx-summary__count">{compactNumber(total)}</span>
    </button>
  );
}

/* ── The picker ────────────────────────────────────────────────────────────── */

/**
 * Hover on a pointer device, long-press on touch, and always reachable by
 * keyboard through the button itself — a hover-only picker is unusable on a
 * phone, which is where most people will read this.
 */
export function ReactionButton({ current, onSelect, disabled }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const closeTimer = useRef(null);
  const pressTimer = useRef(null);

  const active = current ? REACTIONS[current] : null;
  const Icon = active?.icon ?? ThumbsUp;

  const show = useCallback(() => {
    clearTimeout(closeTimer.current);
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos({
      // Clamped so the picker never hangs off the edge on a narrow phone.
      left: Math.min(Math.max(r.left + r.width / 2, 130), window.innerWidth - 130),
      top: r.top - 8,
    });
    setOpen(true);
  }, []);

  const hide = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  }, []);

  useEffect(() => () => {
    clearTimeout(closeTimer.current);
    clearTimeout(pressTimer.current);
  }, []);

  // Close on scroll: the picker is positioned in viewport coordinates, so a
  // scroll would otherwise leave it floating over unrelated content.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', close, { capture: true });
  }, [open]);

  const choose = (key) => {
    setOpen(false);
    // Selecting the reaction you already have removes it, which is what a
    // second tap on any toggle should do.
    onSelect(current === key ? null : key);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`post__action rx-trigger${current ? ' is-active' : ''}`}
        style={current ? { color: active.colour } : undefined}
        disabled={disabled}
        onClick={() => choose(current ?? 'useful')}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onPointerDown={(e) => {
          if (e.pointerType !== 'touch') return;
          pressTimer.current = setTimeout(show, 350);
        }}
        onPointerUp={() => clearTimeout(pressTimer.current)}
        onPointerCancel={() => clearTimeout(pressTimer.current)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={current ? `Reacted: ${active.label}. Change or remove.` : 'React to this post'}
      >
        <Icon size={18} fill={current ? 'currentColor' : 'none'} aria-hidden="true" />
        <span>{current ? active.label : 'React'}</span>
      </button>

      {open && pos && createPortal(
        <div
          className="rx-picker"
          style={{ left: pos.left, top: pos.top }}
          onMouseEnter={() => clearTimeout(closeTimer.current)}
          onMouseLeave={hide}
          role="menu"
          aria-label="Choose a reaction"
        >
          {REACTION_ORDER.map((key) => {
            const R = REACTIONS[key];
            const I = R.icon;
            return (
              <button
                key={key}
                type="button"
                role="menuitem"
                className={`rx-picker__item${current === key ? ' is-on' : ''}`}
                onClick={() => choose(key)}
                title={R.label}
              >
                <span className="rx-picker__dot" style={{ background: R.colour }}>
                  <I size={16} strokeWidth={2.5} aria-hidden="true" />
                </span>
                <span className="rx-picker__label">{R.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

export const REACTIONS_CSS = `
.rx-summary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 2px 4px; margin-bottom: 8px;
  border: 0; background: transparent; cursor: pointer;
  font: inherit; font-size: var(--text-xs); color: var(--ink-faint);
  border-radius: var(--radius-sm);
}
.rx-summary:hover { color: var(--ink); text-decoration: underline; }
.rx-summary__icons { display: inline-flex; }
.rx-summary__chip {
  width: 18px; height: 18px; border-radius: 50%;
  display: grid; place-items: center; color: #fff;
  border: 1.5px solid var(--card);
}
/* Overlap, so a row of five does not become a stripe. */
.rx-summary__chip + .rx-summary__chip { margin-left: -6px; }
.rx-summary__count { font-variant-numeric: tabular-nums; }

.rx-trigger.is-active { font-weight: 600; }

/*
 * THE PICKER USED TO FLASH TO THE SIDE BEFORE JUMPING ABOVE THE BUTTON.
 *
 * It carried translate(-50%, -100%) for positioning AND the shared pop-in
 * animation, whose keyframes end on "transform: none". An animation overrides
 * the base value for its whole duration, so for 140ms the translate was simply
 * gone: the picker rendered with its top-left corner at the anchor point, down
 * and to the right, and only snapped into place when the animation finished.
 *
 * Fixed by animating opacity and scale INSIDE the required translate, in a
 * keyframe of its own. Nothing here may ever animate transform without carrying
 * the translate through every single frame.
 *
 * NOTE: this whole block is inside a JS template literal. No backticks.
 */
.rx-picker {
  position: fixed;
  transform: translate(-50%, -100%);
  z-index: var(--z-dropdown);
  display: flex; gap: 2px; padding: 6px;
  background: var(--card); border: 1px solid var(--line-strong);
  border-radius: var(--radius-full);
  box-shadow: var(--shadow-lg);
  transform-origin: 50% 100%;
  animation: rx-pop 140ms ease both;
}
@keyframes rx-pop {
  from { opacity: 0; transform: translate(-50%, -100%) scale(0.96); }
  to   { opacity: 1; transform: translate(-50%, -100%) scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .rx-picker { animation: none; }
}
.rx-picker__item {
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  padding: 6px 8px; min-width: 54px;
  border: 0; background: transparent; cursor: pointer;
  border-radius: var(--radius-sm);
  transition: background var(--transition), transform var(--transition);
}
.rx-picker__item:hover { background: var(--bg-alt); transform: translateY(-2px); }
.rx-picker__item.is-on { background: var(--primary-tint); }
.rx-picker__dot {
  width: 30px; height: 30px; border-radius: 50%;
  display: grid; place-items: center; color: #fff;
}
.rx-picker__label {
  font-size: 10px; color: var(--ink-faint); line-height: 1; white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
  .rx-picker { animation: none; }
  .rx-picker__item:hover { transform: none; }
}
`;
